import React from 'react';
import { motion } from 'framer-motion';
import { Sprout, Shield, AlertTriangle } from 'lucide-react';
import { NatureCard } from '../shared/NatureCard';
import './QuickStatsGrid.css';

interface QuickStatsGridProps {
  nativeCount: number;
  nativeProportion: number;
  endemicCount: number;
  threatenedCount: number;
}

export const QuickStatsGrid: React.FC<QuickStatsGridProps> = ({
  nativeCount,
  nativeProportion,
  endemicCount,
  threatenedCount,
}) => {
  const stats = [
    {
      icon: <Sprout size={32} />,
      value: nativeCount,
      label: 'Native Trees',
      percentage: (nativeProportion * 100).toFixed(1),
      description: 'Indigenous to the region',
      color: 'forest',
      gradient: true,
    },
    {
      icon: <Shield size={32} />,
      value: endemicCount,
      label: 'Endemic Trees',
      badge: 'Protected',
      description: 'Unique to India',
      color: 'endemic',
      gradient: true,
    },
    {
      icon: <AlertTriangle size={32} />,
      value: threatenedCount,
      label: 'Threatened Trees',
      badge: threatenedCount > 0 ? 'Conservation Priority' : 'None',
      description: 'Require special protection',
      color: 'threatened',
      gradient: false,
    },
  ];

  return (
    <section className="quick-stats">
      <div className="quick-stats__container">
        <motion.h2
          className="quick-stats__title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Conservation Snapshot
        </motion.h2>

        <div className="quick-stats__grid">
          {stats.map((stat, index) => (
            <NatureCard
              key={index}
              className="quick-stats__card"
              hoverable={true}
              delay={index * 0.1}
            >
              <div className={`quick-stats__icon quick-stats__icon--${stat.color}`}>
                {stat.icon}
              </div>

              <div className="quick-stats__content">
                <div className="quick-stats__value">{stat.value}</div>
                <div className="quick-stats__label">{stat.label}</div>

                {stat.percentage && (
                  <div className="quick-stats__percentage">
                    {stat.percentage}% of total
                  </div>
                )}

                {stat.badge && !stat.percentage && (
                  <div className={`quick-stats__badge quick-stats__badge--${stat.color}`}>
                    {stat.badge}
                  </div>
                )}

                <p className="quick-stats__description">{stat.description}</p>
              </div>

              {stat.gradient && (
                <div className={`quick-stats__gradient quick-stats__gradient--${stat.color}`} />
              )}
            </NatureCard>
          ))}
        </div>
      </div>
    </section>
  );
};

export default QuickStatsGrid;
